import type { ServerHttpRequest, ServerWebExchange } from '../../../../../types/web/server';
import type { ReadonlyHttpHeaders } from '../../../../../types/web/http';
import type { ServerRequest, ServerRequestHeaders } from './types.ts';
import { MIMEType } from 'node:util';
import { MediaType } from '../../../../http/media-type.ts';
import { getContentType } from '../../../../http/headers.ts';
import type { HttpMessageReader } from '../../../../http/codec/types.ts';

export default {
    create: (exchange: ServerWebExchange, messageReaders: readonly HttpMessageReader[]) : ServerRequest => {
        return new DefaultServerRequest(exchange, messageReaders);
    }
}

class DefaultServerRequestHeaders implements ServerRequestHeaders {
    readonly #headers: ReadonlyHttpHeaders;
    constructor(headers: ReadonlyHttpHeaders) {
        this.#headers = headers;
    }

    accept(): MIMEType[] {
        return MediaType.parseMediaTypes(...this.#headers.list('accept'));
    }

    contentLength(): number | undefined {
        const value = this.#headers.one('content-length');
        return value !== undefined ? Number.parseInt(value) : undefined;
    }

    contentType(): MIMEType | undefined {
        return getContentType(this.#headers);
    }

    header(name: string): string[] {
        return this.#headers.list(name);
    }

    firstHeader(name: string): string | undefined {
        const list = this.#headers.list(name);
        return list.length > 0 ? list[0] : undefined;
    }

    asHttpHeaders(): ReadonlyHttpHeaders {
        return this.#headers;
    }
}

export class DefaultServerRequest implements ServerRequest {
    readonly exchange: ServerWebExchange;
    readonly messageReaders: readonly HttpMessageReader[];
    readonly #headers: ServerRequestHeaders;
    constructor(exchange: ServerWebExchange, messageReaders: readonly HttpMessageReader[]) {
        this.exchange = exchange;
        this.messageReaders = [...messageReaders];
        this.#headers = new DefaultServerRequestHeaders(exchange.request.headers);
    }

    get attributes(): Map<string, unknown> {
        return this.exchange.attributes;
    }

    get headers() {
        return this.#headers;
    }

    get method() {
        return this.request.method;
    }

    get URL() {
        return this.request.URL;
    }

    get requestPath() {
        return this.request.requestPath;
    }

    queryParam(name: string): string | null {
        return this.request.URL.searchParams.get(name);
    }

    get principal() {
        return this.exchange.principal();
    }

    get request(): ServerHttpRequest {
        return this.exchange.request;
    }
}
